// Numbered yellow disc sprites for stage markers — see stageMarkers.ts for
// the feature data. Drawn via canvas (same technique as the land-access hatch
// patterns in base_map_dialog/helpers/LandAccessOverlay.ts).
//
// The number is baked INTO the disc image rather than drawn as a separate
// `text-field` on the same symbol layer. Mapbox renders a symbol layer's
// icons and text as two separate passes — ALL icons in the layer first, THEN
// ALL text — even when icon-image and text-field are on the same layer and
// even for the same feature. So two overlapping badges used to draw as
// disc → disc → number → number instead of each badge's disc+number staying
// together. Baking the number into the icon means there's only one draw pass
// per marker: whichever badge wins the overlap shows its own complete
// disc+number as a single atomic image.

export const STAGE_MARKER_ICON_PREFIX = 'hike-stage-marker-';

// Physical canvas px at pixelRatio 2 → 24 logical px, matching the previous
// circle-radius 11 (+ stroke) badge size.
const CANVAS_SIZE = 48;
const RADIUS = 19;
const STROKE_WIDTH = 5;

function buildDiscImageData(label) {
  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_SIZE;
  canvas.height = CANVAS_SIZE;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

  ctx.beginPath();
  ctx.arc(CANVAS_SIZE / 2, CANVAS_SIZE / 2, RADIUS, 0, Math.PI * 2);
  ctx.fillStyle = '#fddf00';
  ctx.fill();
  ctx.lineWidth = STROKE_WIDTH;
  ctx.strokeStyle = '#17181d';
  ctx.stroke();

  ctx.fillStyle = '#17181d';
  ctx.font = 'bold 24px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(label), CANVAS_SIZE / 2, CANVAS_SIZE / 2 + 1);

  const imageData = ctx.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE);
  return { width: CANVAS_SIZE, height: CANVAS_SIZE, data: new Uint8Array(imageData.data.buffer) };
}

/** The Mapbox image id for a given stage number's badge — matches the
 *  `icon-image` expression built in HikeMap.vue. */
export function stageMarkerIconId(stageNumber) {
  return `${STAGE_MARKER_ICON_PREFIX}${stageNumber}`;
}

/**
 * Registers a disc sprite per distinct stage number if it isn't already on
 * the map. A style switch (base map change) clears all custom images, so
 * this must be re-checked every time layers are (re)added, not just once.
 */
export function ensureStageMarkerIcons(map, stageNumbers) {
  if (!map) return;
  const uniqueNumbers = Array.from(new Set(stageNumbers));
  uniqueNumbers.forEach((stageNumber) => {
    const id = stageMarkerIconId(stageNumber);
    if (map.hasImage(id)) return;
    map.addImage(id, buildDiscImageData(stageNumber), { pixelRatio: 2 });
  });
}
